import { Capacitor } from '@capacitor/core'
import { isNativeApp, isNativePushEnabled } from '../utils/platform'
import {
  clearDeliveredNotificationsByTag,
  registerIncomingCallActionType,
  registerPushToken,
  registerSessionCheckInActionType,
} from './push-notification-service'
import {
  resolveSessionCheckInActionRoute,
  resolveSessionReminderRoute,
} from '../features/sessions/session-reminder-handler'
import {
  isDocumentForegrounded,
  resolveForegroundSessionBanner,
} from '../features/sessions/utils/foreground-session-push'
import { showSessionPushBanner } from '../stores/session-push-banner-store'
import {
  resolveForegroundForumBanner,
  resolveForumPushRoute,
} from '../features/forum/utils/forum-push-routing'
import {
  resolveForegroundConsentRequestBanner,
  resolveSessionRecordingPushRoute,
} from '../features/session-recording/utils/recording-push'
import { SETUP_REQUEST_PUSH_TYPE } from '@shared/setup/admin-requests'
import { ACCOUNT_NOTICE_PUSH_TYPE, accountNoticeRoute } from '@shared/users/account-notice'
import { pushDiagnostics } from '../stores/push-diagnostics-store'
import { waitForDeviceSessionCheck } from '../stores/device-session-store'
import {
  getVoiceCallSession,
  isTerminalCallPhase,
  subscribeVoiceCallSession,
} from '../stores/voice-call-store'
import { announcementPushPreview } from '@shared/announcements/body-preview'
import {
  CALL_PUSH_SIGNAL_VERSION,
  isNewerCallPushContract,
} from '@shared/calls/push-signal-version'

/**
 * Shared, React-independent push-registration singleton.
 *
 * The previous design buried every meaningful step (permission request,
 * `register()`, and the diagnostic "Re-register" wiring) at the end of a long
 * `await` chain inside a single `useEffect` IIFE in `use-push-notifications.ts`.
 * If the hook never armed (auth timing) or any earlier native call stalled,
 * permission was never requested, `register()` never ran, and the diagnostic
 * reported "push hook not active".
 *
 * This module owns the entire flow so it can be triggered DIRECTLY from:
 *   - the push hook (on sign-in + on app foreground, with retry)
 *   - the on-device diagnostic "Re-register" button
 *   - the notification-permission primer
 *
 * It is idempotent and concurrency-guarded, so calling it repeatedly is safe.
 */

type PushPlatform = 'ANDROID' | 'IOS'
type PushNotificationsPlugin = typeof import('@capacitor/push-notifications').PushNotifications

/** Deep-link handler installed by the push hook (so the service stays React-free). */
type PushNavigationHandler = (path: string) => void
let navigationHandler: PushNavigationHandler | null = null
export function setPushNavigationHandler(fn: PushNavigationHandler | null): void {
  navigationHandler = fn
}

let listenersArmed = false
let registeredToken: string | null = null
/** Token whose backend sync is in flight (not yet in `registeredToken`). */
let syncingToken: string | null = null
/**
 * Bumped on sign-out. A sync (or its retry) started under an older generation
 * belongs to the signed-out account and must not mark the token registered.
 */
let signOutGeneration = 0
let inFlight: Promise<void> | null = null
let sawTokenOrError = false
let watchdogTimer: ReturnType<typeof setTimeout> | null = null

/** Only the app's own `/?setup=…` links are followed from a setup-request push. */
export function setupRequestRoute(data: Record<string, string>): string {
  if (typeof data.path === 'string' && /^\/\?setup=[a-z-]+$/.test(data.path)) return data.path
  return data.kind === 'consent' ? '/?setup=recording-consent' : '/?setup=overview'
}

/** Capacitor permission states that should trigger a system prompt. */
function shouldRequestPushPermission(receive: string | undefined): boolean {
  return receive === 'prompt' || receive === 'prompt-with-rationale'
}

/** Registration reasons that come from a person's tap and may show the OS dialog. */
export function mayPromptForPush(reason: string | undefined): boolean {
  return reason === 'primer' || reason === 'manual'
}

function resolvePlatform(): PushPlatform | null {
  const platform = Capacitor.getPlatform()
  if (platform === 'android') return 'ANDROID'
  if (platform === 'ios') return 'IOS'
  return null
}

/**
 * Android notification channels. Channel ids must match the backend FCM
 * payloads (publish-voice-call-signal / notify-message-push Lambdas). Created
 * best-effort and OFF the critical registration path so a stalled native
 * createChannel can never block permission/register().
 */
async function createAndroidChannels(push: PushNotificationsPlugin): Promise<void> {
  await push.createChannel({
    id: 'incoming_calls',
    name: 'Incoming calls',
    description: 'Rings and vibrates like a phone call',
    importance: 5,
    visibility: 1,
    sound: 'ringtone_default.wav',
    vibration: true,
    lights: true,
  })
  await push.createChannel({
    id: 'messages',
    name: 'Messages',
    description: 'New chat messages',
    importance: 4,
    visibility: 1,
    vibration: true,
  })
  await push.createChannel({
    id: 'missed_calls',
    name: 'Missed calls',
    importance: 3,
    visibility: 1,
  })
}

/** Push the FCM/APNs token to the backend, retrying on transient auth/network errors. */
async function syncToken(
  token: string,
  platform: PushPlatform,
  attempt = 0,
  generation = signOutGeneration,
): Promise<void> {
  if (generation !== signOutGeneration) return
  if (registeredToken === token) return
  if (attempt === 0) pushDiagnostics.setBackendStatus('pending')
  // A device signed out remotely signs itself out after this check; its token stays off.
  if (!(await waitForDeviceSessionCheck())) {
    pushDiagnostics.setBackendStatus('error', 'this device was signed out — token not sent')
    return
  }
  if (generation !== signOutGeneration || registeredToken === token) return
  syncingToken = token
  try {
    await registerPushToken(token, platform)
    if (generation !== signOutGeneration) return
    syncingToken = null
    registeredToken = token
    console.info('[push] Token registered with backend', token.slice(0, 8))
    pushDiagnostics.setBackendStatus('success')
    // Link token to Manage devices row so remote revoke can unregister push.
    void linkPushTokenToDevice(token)
  } catch (err) {
    if (generation !== signOutGeneration) return
    const message = err instanceof Error ? err.message : String(err)
    console.warn('[push] registerPushToken failed', err)
    pushDiagnostics.setBackendStatus('error', `attempt ${attempt + 1}: ${message}`)
    // Auth session may not be ready on cold start — retry with backoff so a slow
    // cold start or a transient AppSync error doesn't permanently strand the token.
    if (attempt < 15) {
      const delayMs = Math.min(60_000, 2000 * 2 ** attempt)
      window.setTimeout(() => void syncToken(token, platform, attempt + 1, generation), delayMs)
    }
  }
}

async function linkPushTokenToDevice(token: string): Promise<void> {
  try {
    const { heartbeatMyDevice } = await import('./appsync-client')
    const { getDeviceId, primeDeviceId } = await import('../utils/device-id')
    await primeDeviceId()
    await heartbeatMyDevice({ deviceId: getDeviceId(), pushTokenRef: token })
  } catch {
    /* device registry may not be deployed yet — non-fatal */
  }
}

/**
 * Build the in-app route for an announcement/like push so the tap opens the
 * SPECIFIC post (and comment) rather than just the channel. The backend already
 * sends a `path` for the channel; we append the post/comment query params here
 * so older payloads (path only) still deep-link to the exact target.
 */
function announcementDeepLink(data: Record<string, string>): string {
  const base = data.path || '/announcements/all'
  if (!data.postId) return base
  const [pathname, existingQuery] = base.split('?')
  const params = new URLSearchParams(existingQuery)
  params.set('post', data.postId)
  if (data.commentId) params.set('comment', data.commentId)
  return `${pathname}?${params.toString()}`
}

/**
 * Route a tap on an "Incoming call" notification (VOICE_CALL_INVITE /
 * VIDEO_CALL_INVITE). Previously these taps had no handler at all, so the app
 * simply resumed wherever it last was — usually the DM thread with the caller —
 * instead of the answer UI.
 *
 * The incoming-call answer UI is the global VoiceCallOverlay/GroupCallOverlay,
 * which renders automatically whenever the voice-call store holds a live
 * session; it needs no route change. So the correct behavior is:
 *  - a live (ringing or already-answered) session exists, or shows up shortly
 *    after the tap wakes the app (AppSync INVITE re-delivery / native pending
 *    invite consumption on foreground) → do NOT navigate anywhere; the overlay
 *    is the surface.
 *  - the call is already over (terminal session, or no session materializes
 *    within the ring-rebuild window) → land on the Calls page in missed-call
 *    context, NOT the conversation thread.
 */
function routeCallInviteTap(callId: string, joinCallId?: string): void {
  const matchesCall = (sessionCallId: string) =>
    !callId || sessionCallId === callId || (Boolean(joinCallId) && sessionCallId === joinCallId)
  const goToCalls = () =>
    navigationHandler?.(callId ? `/calls?call=${encodeURIComponent(callId)}` : '/calls')

  const current = getVoiceCallSession()
  if (current && !isTerminalCallPhase(current.phase)) return
  if (current && matchesCall(current.callId)) {
    // This exact call already ended — missed-call context immediately.
    goToCalls()
    return
  }

  // No live session yet: the tap may have just woken/foregrounded the app and
  // the session rebuild (subscription reconnect / pending native invite) is in
  // flight. Wait briefly for it before concluding the call is over.
  let settled = false
  let timer: ReturnType<typeof setTimeout> | null = null
  const unsubscribe = subscribeVoiceCallSession(() => {
    const session = getVoiceCallSession()
    if (!session || isTerminalCallPhase(session.phase)) return
    settled = true
    unsubscribe()
    if (timer) clearTimeout(timer)
  })
  timer = setTimeout(() => {
    if (settled) return
    settled = true
    unsubscribe()
    goToCalls()
  }, 5000)
}

/** Stable notification id for a single announcement post (replaces FCM tray entry). */
function announcementNotificationId(postId: string): number {
  let hash = 0
  for (let i = 0; i < postId.length; i++) {
    hash = (hash * 31 + postId.charCodeAt(i)) | 0
  }
  return Math.abs(hash) || 2
}

/**
 * Older backend builds sent raw structured-body markers in the FCM body. When
 * the app is foregrounded, swap the tray entry for a cleaned local notification.
 */
async function replaceAnnouncementNotification(input: {
  postId: string
  title: string
  rawBody: string
  data: Record<string, string>
}): Promise<void> {
  const fallbackTitle = input.title.replace(/^📣\s*/, '').trim()
  const cleanBody = announcementPushPreview(input.rawBody, fallbackTitle)
  if (!cleanBody || cleanBody === input.rawBody) return

  const tag = `announcement-${input.postId}`
  try {
    await clearDeliveredNotificationsByTag(tag)
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    await LocalNotifications.schedule({
      notifications: [
        {
          id: announcementNotificationId(input.postId),
          title: input.title,
          body: cleanBody,
          channelId: 'messages',
          extra: input.data,
        },
      ],
    })
  } catch {
    /* best-effort — backend preview is the primary fix */
  }
}

/**
 * Add the FCM/APNs listeners exactly once. The `registration` listener MUST be
 * added before `register()` so the token event is never missed. Idempotent.
 */
async function armListeners(push: PushNotificationsPlugin, platform: PushPlatform): Promise<void> {
  if (listenersArmed) return
  listenersArmed = true

  await push.addListener('registration', (event) => {
    sawTokenOrError = true
    if (watchdogTimer) clearTimeout(watchdogTimer)
    console.info('[push] FCM/APNs registration event', event.value.slice(0, 8))
    pushDiagnostics.setToken(event.value)
    void syncToken(event.value, platform)
  })

  await push.addListener('registrationError', (err) => {
    sawTokenOrError = true
    if (watchdogTimer) clearTimeout(watchdogTimer)
    const message =
      (err as { error?: string })?.error ??
      (err instanceof Error ? err.message : JSON.stringify(err))
    console.warn(
      '[push] FCM registration failed — check google-services.json and Firebase Play signing SHA-1',
      err,
    )
    pushDiagnostics.setRegistrationError(message)
  })

  await push.addListener('pushNotificationReceived', (notification) => {
    const data = (notification.data ?? {}) as Record<string, string>
    // Tolerant contract-version check (see docs/call-release-protocol.md):
    // absent/unknown = old backend, fine; NEWER than this build = deployed
    // lambdas moved ahead of the store build — proceed, but log loudly so
    // field drift is diagnosable.
    if (data.type?.includes('CALL') && isNewerCallPushContract(data.signalVersion)) {
      console.error(
        `[push] call push signalVersion=${data.signalVersion} is newer than this build ` +
          `understands (${CALL_PUSH_SIGNAL_VERSION}) — proceeding tolerantly; update the app`,
      )
    }
    if (data.type === 'VOICE_CALL_DISMISS') {
      const tag = data.tag || (data.callId ? `call-${data.callId}` : null)
      if (tag) void clearDeliveredNotificationsByTag(tag)
      return
    }
    if (data.type === 'ANNOUNCEMENT' && data.postId) {
      const rawBody = notification.body ?? data.body ?? ''
      if (rawBody.includes('ascend-announcement:')) {
        void replaceAnnouncementNotification({
          postId: data.postId,
          title: notification.title ?? '📣 Announcement',
          rawBody,
          data,
        })
      }
    }
    // Session-event data pushes: native skips the tray while foregrounded and
    // still delivers this event (including mid-call). Show the in-app banner
    // only on that foreground path so a background tray is not doubled.
    // iOS already presents session pushes as APNs alerts in the foreground
    // (`presentationOptions` includes 'alert'), so skip the in-app banner there.
    const sessionBanner = resolveForegroundSessionBanner(
      { title: notification.title, body: notification.body, data },
      { appForegrounded: isDocumentForegrounded(), platform },
    )
    if (sessionBanner && platform === 'ANDROID') showSessionPushBanner(sessionBanner)
    // Forum approval request / decision: Android never shows the system
    // notification message while foregrounded, so surface the same in-app banner.
    const forumBanner = resolveForegroundForumBanner(
      { title: notification.title, body: notification.body, data },
      { appForegrounded: isDocumentForegrounded(), platform },
    )
    if (forumBanner && platform === 'ANDROID') showSessionPushBanner(forumBanner)
    const consentBanner = resolveForegroundConsentRequestBanner(
      { title: notification.title, body: notification.body, data },
      { appForegrounded: isDocumentForegrounded(), platform },
    )
    if (consentBanner) showSessionPushBanner(consentBanner)
  })

  await push.addListener('pushNotificationActionPerformed', (event) => {
    const data = (event.notification.data ?? {}) as Record<string, string>
    // One-tap "Check in" action (arrival push / start reminder) — must be
    // checked BEFORE the generic session deep-link: the action lands on the
    // session AND performs the check-in immediately (undo snackbar follows).
    // Cold start is covered: Capacitor retains this event from the launch
    // intent until the listener attaches.
    const checkInRoute = resolveSessionCheckInActionRoute(data, event.actionId)
    if (checkInRoute) {
      navigationHandler?.(checkInRoute)
      return
    }
    const sessionRoute = resolveSessionReminderRoute(data)
    if (sessionRoute) {
      navigationHandler?.(sessionRoute)
      return
    }
    // Premium Session Recording "draft ready" → the review page; a consent request → its accept screen.
    const recordingRoute = resolveSessionRecordingPushRoute(data)
    if (recordingRoute) {
      navigationHandler?.(recordingRoute)
      return
    }
    // Forum approval request → the moderation screen; decision → the post.
    const forumRoute = resolveForumPushRoute(data)
    if (forumRoute) {
      navigationHandler?.(forumRoute)
      return
    }
    if (data.type === 'MESSAGE' && data.conversationId) {
      const module = data.channelGroupId === 'groups' ? 'groups' : 'direct'
      navigationHandler?.(`/messages/${module}?thread=${encodeURIComponent(data.conversationId)}`)
    } else if (data.type === 'ANNOUNCEMENT') {
      navigationHandler?.(announcementDeepLink(data))
    } else if (data.type === 'LIKE' || data.type === 'COMMENT') {
      // Like/comment notifications carry a fully-built path (post + optional comment).
      navigationHandler?.(data.path || announcementDeepLink(data))
    } else if (data.type === SETUP_REQUEST_PUSH_TYPE) {
      // Admin "Reset setup" / "Resend consent": open setup (on the consent step for a resend).
      navigationHandler?.(setupRequestRoute(data))
    } else if (data.type === ACCOUNT_NOTICE_PUSH_TYPE) {
      // Reactivated / two-factor login reset: only the app's own paths are followed.
      navigationHandler?.(accountNoticeRoute(data) ?? '/')
    } else if (data.type === 'MEMBER_REVIEW') {
      // Owner-review of a new pending member → the Admin pending-members queue.
      navigationHandler?.(data.path || '/admin#pending-members')
    } else if (data.type === 'VOICE_CALL_MISSED') {
      navigationHandler?.(data.callId ? `/calls?call=${encodeURIComponent(data.callId)}` : '/calls')
    } else if (data.type === 'VOICE_CALL_INVITE' || data.type === 'VIDEO_CALL_INVITE') {
      // Still ringing → the global incoming-call overlay is the answer surface
      // (no navigation); already over → Calls page missed-call context. Never
      // the conversation thread.
      routeCallInviteTap(data.callId ?? '', data.joinCallId || undefined)
    }
  })
}

/**
 * Register for an FCM/APNs token, asking for POST_NOTIFICATIONS (Android 13+)
 * / APNs authorization first when the reason is a tap. iOS waits for alerts to
 * be allowed; Android registers either way. Safe to call repeatedly and from
 * anywhere. Resolves once register() has been kicked off (the token arrives
 * asynchronously via the `registration` listener and is synced to the backend).
 */
export async function registerForPush(opts?: { reason?: string }): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  const platform = resolvePlatform()
  if (!platform) return
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      if (opts?.reason) pushDiagnostics.note(`registerForPush(${opts.reason})`)
      const { PushNotifications } = await import('@capacitor/push-notifications')

      // Best-effort, OFF the critical path: a stalled native call here must
      // never block permission/register().
      if (platform === 'ANDROID') {
        void createAndroidChannels(PushNotifications).catch((err) => {
          console.warn('[push] createChannel failed', err)
        })
      }
      void registerIncomingCallActionType().catch(() => {
        /* best-effort */
      })
      // iOS: registers the `session_checkin` UNNotificationCategory so remote
      // alerts carrying that aps.category show the "Check in" action button.
      void registerSessionCheckInActionType().catch(() => {
        /* best-effort */
      })

      // Listeners BEFORE register() so the token event is never missed.
      await armListeners(PushNotifications, platform)

      let permStatus = await PushNotifications.checkPermissions()
      pushDiagnostics.setPermission(permStatus.receive)
      // The OS dialog only ever follows a tap (setup's "Turn on alerts", or the
      // diagnostics re-register): sign-in / foreground registration never asks.
      const undecided = shouldRequestPushPermission(permStatus.receive)
      if (undecided && mayPromptForPush(opts?.reason)) {
        permStatus = await PushNotifications.requestPermissions()
        pushDiagnostics.setPermission(permStatus.receive)
      }
      if (permStatus.receive !== 'granted') {
        // Android still registers so calls ring: call invites are data messages
        // and the ring is a self-managed CallStyle notification, which Android 13+
        // posts without the alerts permission. Alerts off then only silences
        // messages and announcements.
        if (platform !== 'ANDROID') {
          if (undecided && !mayPromptForPush(opts?.reason)) {
            pushDiagnostics.note('notification permission not decided yet — waiting for setup')
          } else {
            console.warn('[push] Notification permission not granted:', permStatus.receive)
          }
          return
        }
        pushDiagnostics.note(`alerts ${permStatus.receive} — registering so calls still ring`)
      }

      pushDiagnostics.setRegisterCalled()
      sawTokenOrError = false
      await PushNotifications.register()

      // Watchdog: if neither a token nor an error arrives shortly after a
      // successful register(), FCM failed silently — surface WHY instead of
      // sitting on "token: none".
      if (watchdogTimer) clearTimeout(watchdogTimer)
      watchdogTimer = setTimeout(() => {
        if (sawTokenOrError || registeredToken) return
        pushDiagnostics.setRegistrationError(
          'register() succeeded but FCM returned no token and no error within 12s — ' +
            'likely the release signing SHA-1/256 is not in Firebase for this build, ' +
            'Google Play services is unavailable, or google-services.json is misconfigured.',
        )
      }, 12_000)
    } catch (err) {
      console.warn('[push] PushNotifications plugin unavailable — rebuild with google-services.json', err)
      pushDiagnostics.note(
        `push plugin unavailable: ${err instanceof Error ? err.message : String(err)}`,
      )
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/**
 * Sign-out on this device: delete the signed-in user's backend rows for every
 * push target this device holds (FCM/APNs, iOS VoIP, browser Web Push) so it
 * stops ringing for their calls, and forget the synced token so the next
 * account to sign in registers it again. Must run while the session is still
 * valid. Best-effort and time-boxed: a slow network never blocks sign-out
 * (the next account's registration also releases the token server-side).
 *
 * Every request names the signed-out `userId`; the backend ignores an
 * unregister whose caller is someone else, so a request that only reaches it
 * after the next account signed in cannot delete that account's row. Requests
 * not yet sent when the time box expires are dropped.
 */
export async function unregisterPushForSignOut(input: {
  userId: string
  timeoutMs?: number
}): Promise<void> {
  const { userId, timeoutMs = 3000 } = input
  const nativeToken = registeredToken ?? syncingToken
  registeredToken = null
  syncingToken = null
  signOutGeneration += 1
  let expired = false
  const work = (async () => {
    const { unregisterPushToken } = await import('./appsync-client')
    const jobs: Promise<unknown>[] = []
    const send = (token: string, platform: 'ANDROID' | 'IOS' | 'IOS_VOIP' | 'WEB') => {
      if (expired) return
      jobs.push(unregisterPushToken(token, platform, userId))
    }
    const platform = isNativeApp() ? resolvePlatform() : null
    if (nativeToken && platform) send(nativeToken, platform)
    if (platform === 'IOS') {
      const native = await import('./voice-call/native-call')
      // The token this session registered goes out first — it never waits on
      // the native lookup, which can outlast the time box on a cold launch.
      const registered = native.takeRegisteredVoipToken()
      if (registered) send(registered, 'IOS_VOIP')
      const voipToken = await native.getNativeVoipTokenForUnregister().catch(() => null)
      if (voipToken && voipToken !== registered) send(voipToken, 'IOS_VOIP')
    }
    if (!isNativeApp()) {
      const { getWebPushEndpoint } = await import('./web-push/web-push-registration')
      const endpoint = await getWebPushEndpoint()
      if (endpoint) send(endpoint, 'WEB')
    }
    await Promise.allSettled(jobs)
  })()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      expired = true
      resolve()
    }, timeoutMs)
  })
  try {
    await Promise.race([work.catch(() => {}), timeout])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Force a full re-run of the registration flow (used by the on-device
 * diagnostic "Re-register" button). Resets the synced-token guard so the
 * backend mutation runs again.
 */
export async function reRegisterForPush(): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) {
    pushDiagnostics.note('re-register skipped (push not enabled on this build/platform)')
    return
  }
  registeredToken = null
  pushDiagnostics.note('manual re-register requested')
  pushDiagnostics.setBackendStatus('idle')
  await registerForPush({ reason: 'manual' })
}
